/**
 * In-app health banner. Surfaces broken integrations (a rotated UWM credential,
 * a disconnected Gmail, failing borrower emails) at the top of every page so a
 * silent failure can't run for days unnoticed.
 *
 * Dismissals are per-issue and stored in sessionStorage, so hiding one doesn't
 * hide a NEW problem and the banner returns next session while it's still broken.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

interface HealthIssue {
  key: string;
  severity: 'error' | 'warning';
  title: string;
  detail: string;
  href?: string;
  hrefLabel?: string;
}

const DISMISS_KEY = 'mana.health.dismissed';

function readDismissed(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISS_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

export function HealthBanner() {
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const { data } = useQuery<{ issues: HealthIssue[] }>({
    queryKey: ['health-integrations'],
    queryFn: () => api('/api/health/integrations'),
    refetchInterval: 5 * 60_000, // re-check every 5 min
    refetchOnWindowFocus: true,
    retry: false,
  });

  const issues = (data?.issues ?? []).filter((i) => !dismissed.includes(i.key));
  if (issues.length === 0) return null;

  const hide = (key: string) => {
    const next = [...readDismissed(), key];
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    setDismissed(next);
  };

  return (
    <div className="mb-4 space-y-2">
      {issues.map((issue) => {
        const isError = issue.severity === 'error';
        return (
          <div
            key={issue.key}
            role="alert"
            className={
              'flex flex-wrap items-start gap-x-3 gap-y-1.5 rounded-xl border px-4 py-3 text-sm ' +
              (isError
                ? 'border-rose-300 bg-rose-50 text-rose-900'
                : 'border-amber-300 bg-amber-50 text-amber-900')
            }
          >
            <span aria-hidden className="mt-0.5 shrink-0 text-base leading-none">
              {isError ? '⚠' : 'ℹ'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{issue.title}</p>
              <p className={'mt-0.5 break-words text-xs ' + (isError ? 'text-rose-700' : 'text-amber-800')}>
                {issue.detail}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {issue.href && (
                <Link
                  to={issue.href}
                  className={
                    'rounded-md px-2.5 py-1 text-xs font-semibold text-white ' +
                    (isError ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700')
                  }
                >
                  {issue.hrefLabel ?? 'Fix'}
                </Link>
              )}
              <button
                type="button"
                onClick={() => hide(issue.key)}
                title="Dismiss for this session"
                className={
                  'text-lg leading-none ' +
                  (isError ? 'text-rose-400 hover:text-rose-700' : 'text-amber-400 hover:text-amber-700')
                }
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
